import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Sun, Moon, Calendar as CalendarIcon } from 'lucide-react';
import {
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  format,
  isSameDay,
  isToday,
  isFuture
} from 'date-fns';

interface JournalEntry {
  id: string;
  promptType: 'morning' | 'evening';
  completedAt: Date;
  reflectionDetails?: Array<{
    question: string;
    answer: string;
  }>;
}

interface WeekCalendarProps {
  entries: JournalEntry[];
  onDateSelect: (date: Date, entries: JournalEntry[]) => void;
  onShowFullCalendar?: () => void;
}

export const WeekCalendar: React.FC<WeekCalendarProps> = ({
  entries,
  onDateSelect,
  onShowFullCalendar
}) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date()));

  const nextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const prevWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date()));
  };

  const getEntriesForDate = (date: Date): JournalEntry[] => {
    return entries.filter(entry => 
      isSameDay(entry.completedAt, date)
    );
  };

  const hasMorning = (date: Date): boolean => {
    return getEntriesForDate(date).some(e => e.promptType === 'morning');
  };

  const hasEvening = (date: Date): boolean => {
    return getEntriesForDate(date).some(e => e.promptType === 'evening');
  };

  const handleDateClick = (date: Date) => {
    const dateEntries = getEntriesForDate(date);
    if (dateEntries.length > 0) {
      onDateSelect(date, dateEntries);
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const isCurrentWeek = weekDays.some(day => isToday(day));

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 lg:mb-8">
        <button
          onClick={prevWeek}
          className="p-2 lg:p-3 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous week"
        >
          <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6 text-gray-600" />
        </button>
        
        <div className="flex items-center gap-3">
          <h3 className="text-lg lg:text-xl font-bold text-gray-900">
            {format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}
          </h3>
          {!isCurrentWeek && (
            <button
              onClick={goToToday}
              className="text-sm lg:text-base text-blue-600 hover:text-blue-700 font-medium px-3 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              Today
            </button>
          )}
        </div>
        
        <button
          onClick={nextWeek}
          className="p-2 lg:p-3 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next week"
        >
          <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-gray-600" />
        </button>
      </div>

      {/* Week View - Horizontal Scroll on Mobile, Grid on Desktop */}
      <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 lg:overflow-visible lg:mx-0 lg:px-0">
        <div className="flex gap-2 min-w-max lg:grid lg:grid-cols-7 lg:gap-3 lg:min-w-0">
          {weekDays.map((day) => {
            const dayHasEntries = getEntriesForDate(day).length > 0;
            const dayHasMorning = hasMorning(day);
            const dayHasEvening = hasEvening(day);
            const isDayToday = isToday(day);
            const isDayFuture = isFuture(day) && !isDayToday;

            return (
              <button
                key={day.toString()}
                onClick={() => handleDateClick(day)}
                disabled={!dayHasEntries || isDayFuture}
                className={`
                  flex flex-col items-center justify-center w-[72px] h-[100px] lg:w-full lg:h-[140px] rounded-2xl transition-all duration-200
                  ${isDayToday && dayHasEntries ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg scale-105 lg:scale-100 lg:ring-4 lg:ring-blue-300' : ''}
                  ${isDayToday && !dayHasEntries ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300' : ''}
                  ${!isDayToday && dayHasEntries && !isDayFuture ? 'bg-white border-2 border-gray-200 hover:shadow-md hover:border-gray-300 active:scale-95 lg:hover:scale-105 lg:hover:shadow-xl' : ''}
                  ${!dayHasEntries && !isDayToday || isDayFuture ? 'bg-gray-50 border border-gray-200 opacity-40' : ''}
                `}
              >
                {/* Day Name */}
                <div className={`text-[10px] lg:text-xs font-semibold mb-1 lg:mb-2 uppercase tracking-wide ${
                  isDayToday && dayHasEntries ? 'text-blue-100' :
                  isDayToday && !dayHasEntries ? 'text-blue-600' :
                  dayHasEntries ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {format(day, 'EEE')}
                </div>
                
                {/* Day Number */}
                <div className={`text-xl lg:text-3xl font-bold mb-2 lg:mb-4 ${
                  isDayToday && dayHasEntries ? 'text-white' :
                  isDayToday && !dayHasEntries ? 'text-blue-600' :
                  dayHasEntries ? 'text-gray-900' : 'text-gray-400'
                }`}>
                  {format(day, 'd')}
                </div>
                
                {/* Entry Indicators */}
                <div className="flex items-center justify-center gap-1 lg:gap-2">
                  {dayHasMorning && (
                    <div className={`w-5 h-5 lg:w-7 lg:h-7 rounded-full flex items-center justify-center shadow-sm ${
                      isDayToday ? 'bg-white/30 backdrop-blur-sm' : 'bg-gradient-to-br from-yellow-400 to-orange-500'
                    }`}>
                      <Sun className={`w-3 h-3 lg:w-4 lg:h-4 ${isDayToday ? 'text-white' : 'text-white'}`} />
                    </div>
                  )}
                  {dayHasEvening && (
                    <div className={`w-5 h-5 lg:w-7 lg:h-7 rounded-full flex items-center justify-center shadow-sm ${
                      isDayToday ? 'bg-white/30 backdrop-blur-sm' : 'bg-gradient-to-br from-indigo-500 to-purple-600'
                    }`}>
                      <Moon className={`w-3 h-3 lg:w-4 lg:h-4 ${isDayToday ? 'text-white' : 'text-white'}`} />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend & Full Calendar Button */}
      <div className="mt-6 lg:mt-8 pt-5 lg:pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-6 lg:gap-8">
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-sm">
                <Sun className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
              </div>
              <span className="text-sm lg:text-base font-medium text-gray-700">Morning</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                <Moon className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
              </div>
              <span className="text-sm lg:text-base font-medium text-gray-700">Evening</span>
            </div>
          </div>
          
          {onShowFullCalendar && (
            <button
              onClick={onShowFullCalendar}
              className="flex items-center gap-2 px-4 py-2 lg:px-5 lg:py-2.5 text-sm lg:text-base font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <CalendarIcon className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="hidden sm:inline">Full Calendar</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

